/**
 * ConfigStoragePage
 *
 * Admin page to configure the storage adapter for this org.
 * Reads/writes organisation/{org_id}/config/storage in Firestore via
 * the /admin/config/storage endpoints.
 *
 * Route: /settings/config/storage  (admin-only)
 */

import { useCallback, useEffect, useState } from "react";
import useFeedback from "../../hooks/useFeedback";
import SettingsPageShell from "../../components/Settings/SettingsPageShell";
import { PrimaryButton } from "../../components/Button/Buttons";
import Select from "../../components/Inputs/Select";
import Input from "../../components/Inputs/Input";
import Loader from "../../components/Loader/Loader";
import { getServiceConfig, updateServiceConfig } from "../../api/adminConfig";
import { AuthError } from "../../api";
import UIStore from "../../store/uiStore";
import { isDirty } from "../../utils/isDirty";
import { STORAGE_ADAPTER_OPTIONS } from "./configAdapterOptions";

const EMPTY_FORM = {
  adapter_type: "",
  bucket_name: "",
};

const ConfigStoragePage = () => {
  const { showFeedback } = useFeedback();
  const orgAvailable = UIStore((s) => s.orgAvailable);

  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [form, setForm]             = useState(EMPTY_FORM);
  const [originalForm, setOriginalForm] = useState(EMPTY_FORM);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getServiceConfig("storage");
      const loaded = { adapter_type: data.adapter_type ?? "", bucket_name: data.bucket_name ?? "" };
      setForm(loaded);
      setOriginalForm(loaded);
    } catch (err) {
      if (!(err instanceof AuthError)) showFeedback("Failed to load storage config.", "error");
    } finally {
      setLoading(false);
    }
  }, [showFeedback]);

  useEffect(() => {
    if (orgAvailable) fetchConfig();
  }, [orgAvailable, fetchConfig]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateServiceConfig("storage", form);
      setOriginalForm(form);
      showFeedback("Storage config saved.", "success");
    } catch (err) {
      showFeedback(err?.response?.data?.error || "Failed to save storage config.", "error");
    } finally {
      setSaving(false);
    }
  };

  const storageDisabled = form.adapter_type === "noop";

  return (
    <SettingsPageShell
      title="Storage Configuration"
      description="Configure the storage adapter used for file, media, and knowledge base uploads."
    >
      {loading ? (
        <div className="flex justify-center py-16">
          <Loader />
        </div>
      ) : (
        <div className="flex flex-col gap-6 max-w-lg">
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">
              Storage Adapter
            </label>
            <Select
              value={form.adapter_type}
              onChange={(adapterType) => setForm((prev) => ({
                ...prev,
                adapter_type: adapterType,
                bucket_name: adapterType === "noop" ? "" : prev.bucket_name,
              }))}
              options={STORAGE_ADAPTER_OPTIONS}
              placeholder="Not configured"
            />
            <p className="text-[11px] text-ink-gray-400 dark:text-ink-gray-500">
              No-op is intended for installation validation and disables all persistent file operations.
            </p>
          </div>

          {!storageDisabled && (
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">
                Bucket Name
              </label>
              <Input
                type="text"
                placeholder={form.adapter_type === "azure" ? "Container name" : "my-storage-bucket"}
                value={form.bucket_name}
                onChange={(e) => setForm((prev) => ({ ...prev, bucket_name: e.target.value }))}
              />
              <p className="text-[11px] text-ink-gray-400 dark:text-ink-gray-500">
                {form.adapter_type === "azure" ? "Azure Blob container name." : "GCS / S3 bucket name."}
              </p>
            </div>
          )}
          <div className="flex flex-col gap-1.5">
            <p className="text-[11px] text-ink-gray-400 dark:text-ink-gray-500">
              Adapter and bucket changes take effect after all API instances restart. Existing objects are not migrated automatically.
            </p>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <PrimaryButton
              onClick={handleSave}
              disabled={
                saving
                || !form.adapter_type
                || (!storageDisabled && !form.bucket_name.trim())
                || !isDirty(form, originalForm)
              }
            >
              {saving ? "Saving…" : "Save"}
            </PrimaryButton>
          </div>
        </div>
      )}
    </SettingsPageShell>
  );
};

export default ConfigStoragePage;
